import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Languages, Sparkles, Lock, ShieldCheck } from "lucide-react";
import Header from "@user/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChatSidebar } from "@user/components/chat/ChatSidebar";
import { EmptyState, ChatMessage, TypingIndicator, LawyerConversionPrompt } from "@user/components/chat/ChatMessages";
import { ChatComposer } from "@user/components/chat/ChatComposer";

export default function Chat() {
  const { id: paramId } = useParams();
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [currentId, setCurrentId] = useState(paramId || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [language, setLanguage] = useState("english");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadChats = async () => {
    try {
      const res = await api.get("/chats");
      setChats(res.data);
    } catch (e) {
      console.error("[chat] failed to load chat list", e);
    }
  };

  const loadChat = async (id) => {
    try {
      const res = await api.get(`/chats/${id}`);
      setMessages(res.data.messages || []);
      setCurrentId(id);
    } catch {
      setMessages([]);
    }
  };

  useEffect(() => {
    if (!user || loading) return;
    loadChats();
  }, [user, loading]);

  useEffect(() => {
    if (paramId && user) loadChat(paramId);
  }, [paramId, user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  // "+ New chat" is a pure client-side reset — no empty chat persisted.
  // Backend chat is created on-demand inside send() when the first message is sent.
  const startNewChat = () => {
    setCurrentId(null);
    setMessages([]);
    setInput("");
    if (paramId) navigate("/chat");
  };

  const createChat = async () => {
    const res = await api.post("/chats", {});
    setChats([res.data, ...chats]);
    setCurrentId(res.data.id);
    setMessages([]);
    navigate(`/chat/${res.data.id}`);
    return res.data.id;
  };

  const deleteChat = async (id) => {
    await api.delete(`/chats/${id}`);
    setChats((x) => x.filter((c) => c.id !== id));
    if (currentId === id) {
      setCurrentId(null);
      setMessages([]);
      navigate("/chat");
    }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text) return;
    if (!user) {
      toast.info("Please sign in to save and send messages");
      login();
      return;
    }
    setSending(true);
    let chatId = currentId;
    try {
      if (!chatId) chatId = await createChat();
      setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: text }]);
      setInput("");
      const res = await api.post(`/chats/${chatId}/messages`, { content: text, language });
      setMessages((m) => {
        const filtered = m.filter((x) => !String(x.id).startsWith("tmp-"));
        return [...filtered, res.data.user_message, res.data.assistant_message];
      });
      // refresh chat title in sidebar
      loadChats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center"><div className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-[#1E3A8A] animate-spin" /></div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="h-14 w-14 mx-auto rounded-2xl bg-[#DBEAFE] text-[#1E3A8A] grid place-items-center">
            <Lock size={24} />
          </div>
          <h1 className="mt-5 text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Sign in to ask a legal question</h1>
          <p className="mt-3 text-slate-600">Your chats are saved privately and securely to your account.</p>
          <Button
            onClick={login}
            data-testid="chat-signin-btn"
            className="mt-6 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-12 px-7 font-semibold"
          >
            Sign in / Sign up
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <Header />
      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 grid lg:grid-cols-12 gap-6">
        <ChatSidebar chats={chats} currentId={currentId} onNew={startNewChat} onOpen={(id) => navigate(`/chat/${id}`)} onDelete={deleteChat} />

        {/* Main chat */}
        <section className="lg:col-span-9 flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden min-h-[70vh]">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50/50">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-[#1E3A8A] text-white grid place-items-center"><Sparkles size={16} /></div>
              <div>
                <p className="text-sm font-bold text-slate-900">Advoset AI</p>
                <p className="text-xs text-slate-500">Plain-language legal help</p>
              </div>
            </div>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="lang-select">
                <Languages size={14} className="mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="english">English</SelectItem>
                <SelectItem value="hindi">हिंदी</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* AI disclaimer banner */}
          <div
            data-testid="ai-disclaimer-banner"
            className="flex items-start gap-2 px-5 py-2.5 bg-amber-50/70 border-b border-amber-100 text-[11px] text-amber-900"
          >
            <ShieldCheck size={14} className="shrink-0 mt-0.5 text-amber-600" />
            <span>
              <span className="font-bold">AI-generated guidance.</span>{" "}
              For accurate legal advice,{" "}
              <Link to="/lawyers" className="underline font-semibold hover:text-amber-950">consult a verified lawyer</Link>.
            </span>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4" data-testid="messages-container">
            {messages.length === 0 && <EmptyState onSuggest={send} />}

            {messages.map((m) => <ChatMessage key={m.id} m={m} />)}

            {sending && <TypingIndicator />}

            {!sending && messages.filter((m) => m.role === "assistant").length >= 1 && <LawyerConversionPrompt />}

            <div ref={bottomRef} />
          </div>

          <ChatComposer input={input} setInput={setInput} onSend={send} sending={sending} language={language} />
        </section>
      </div>
    </div>
  );
}
